import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { MigrateFromQskErrorCode } from '@/lib/api/client.ts'
import { ApiClient } from '@/lib/api/client.ts'
import { Button } from '@/components/ui/button.tsx'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'

/**
 * QSK → German Method migration landing page.
 *
 * Users arrive from the old QSK system with a signed JWT in the URL. Clicking
 * "Migration abschließen" posts the token to the BFF, which calls the edge
 * function that creates/updates the account and triggers the password-setup
 * email. After a successful migration we redirect the user to the public
 * landing page (germanmethod.com), which renders the post-migration "Fertig!"
 * confirmation modal — `app.germanmethod.com` has no other useful UI for
 * migrated users.
 *
 * Error UX: ApiClient.migrateFromQsk returns a discriminated union; failures
 * arrive with a user-friendly German message and a stable error code. The
 * raw Supabase "Edge Function returned a non-2xx status code" string is
 * intercepted by the API client and never reaches this component.
 */

type MigrationError = {
  code: MigrateFromQskErrorCode
  message: string
}

/**
 * Codes for which "Zum Login" is the obviously useful next step:
 * - USER_NOT_FOUND: token was valid but no QSK record exists; nothing to
 *   migrate, but the user might already have a GM account.
 * - ALREADY_COMPLETE: the migration ran in a previous session; the user just
 *   needs to log in.
 */
const SHOW_LOGIN_LINK_CODES = new Set<MigrateFromQskErrorCode>([
  'MIGRATION_USER_NOT_FOUND',
  'MIGRATION_ALREADY_COMPLETE',
])

export function MigrateFromQskPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [userToken, setUserToken] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<MigrationError | null>(null)

  useEffect(() => {
    const token = searchParams.get('userToken')
    if (!token) {
      navigate('/auth', { replace: true })
      return
    }
    setUserToken(token)
  }, [navigate, searchParams])

  const handleConfirm = async () => {
    if (!userToken) return
    // Reset prior error so retries start with a clean slate.
    setError(null)
    setIsSubmitting(true)

    try {
      const result = await ApiClient.migrateFromQsk(userToken)

      if (!result.success) {
        console.error('[MIGRATE QSK] Failed:', result.errorCode)
        setError({ code: result.errorCode, message: result.messageDe })
        return
      }

      const data = result.data

      // Clean up the URL so a reload doesn't re-attempt the migration.
      window.history.replaceState(null, '', window.location.pathname)

      // Post-migration UX lives on the public landing page. Build the target
      // URL from VITE_LANDINGPAGE_URL (defaults to https://germanmethod.com)
      // and use replace() so the back button doesn't return here with a
      // now-consumed token.
      const landingBase = (
        import.meta.env.VITE_LANDINGPAGE_URL ?? 'https://germanmethod.com'
      ).replace(/\/+$/, '')
      const target = `${landingBase}/?migration=true&email=${encodeURIComponent(data.email)}`
      window.location.replace(target)
    } catch (err) {
      // Defense in depth: ApiClient.migrateFromQsk catches its own errors and
      // returns a structured failure, but if anything truly unexpected slips
      // through we still want a friendly German message.
      console.error('[MIGRATE QSK] Unexpected error:', err)
      setError({
        code: 'UNEXPECTED_RESPONSE',
        message:
          'Bei der Migration ist ein unerwarteter Fehler aufgetreten. Bitte versuche es in ein paar Minuten erneut. Wenn das Problem bleibt, kontaktiere den Support.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!userToken) {
    // First render before the useEffect redirects; avoid flashing content.
    return null
  }

  const showLoginLink = error !== null && SHOW_LOGIN_LINK_CODES.has(error.code)

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Zum German Method wechseln</CardTitle>
          <CardDescription>
            Dein QSK-Lernstand wird übernommen und dein kostenloser
            Premium-Zugang wird freigeschaltet. Wir senden dir eine E-Mail,
            damit du dein Passwort festlegen kannst.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                <span>{error.message}</span>
                {showLoginLink && (
                  <span className="block mt-2">
                    <Link
                      to="/auth"
                      className="underline font-medium"
                    >
                      Zum Login
                    </Link>
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}
          <Button
            className="w-full"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Migration läuft…' : 'Migration abschließen'}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Du erhältst eine E-Mail mit dem Link zur Passwortvergabe.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
